import { useState, useEffect, useContext } from 'react';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  add,
  isToday,
  isSameMonth,
  startOfDay,
  parseISO,
} from 'date-fns';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  MapPinIcon,
  XCircleIcon,
  UserCircleIcon,
  ClockIcon,
} from '@heroicons/react/20/solid';
import { useNavigate } from 'react-router-dom';
import LoadingComponent from './loading';
import { AuthContext } from '../AuthContext';
import Navbar from './Navbar';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function Meetings() {
  const Navigate = useNavigate();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [appointmentDate, setAppointmentDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [selectedTimes, setSelectedTimes] = useState([]);
  const [createdBy, setCreatedBy] = useState('John');
  const [showAppointmentForm, setShowAppointmentForm] = useState(false);

  const user = useContext(AuthContext);

  // Use currentMonth for calendar view
  const startDate = startOfWeek(startOfMonth(currentMonth), { weekStartsOn: 1 });
  const endDate = endOfWeek(endOfMonth(currentMonth), { weekStartsOn: 1 });

  const days = [];
  let day = startOfDay(startDate);
  while (day <= endDate) {
    days.push(day);
    day = add(day, { days: 1 });
  }

  useEffect(() => {
    fetchMeetings(selectedDate);
    // eslint-disable-next-line
  }, []);

  const fetchMeetings = async (date) => {
    try {
      setLoading(true);
      const formattedDate = format(date, 'yyyy-MM-dd');
      const response = await fetch(`https://api.insightos.co.uk/admin/meetings/${formattedDate}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      setMeetings(Array.isArray(data.data) ? data.data : []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const normalizeInternalSlot = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const getMeetingId = (meeting) => meeting.id ?? meeting.ID ?? meeting.meeting_id;

  const previousMonth = () => setCurrentMonth(add(currentMonth, { months: -1 }));
  const nextMonth = () => setCurrentMonth(add(currentMonth, { months: 1 }));

  // When currentMonth changes, update selectedDate and fetch meetings for the first day of the new month
  useEffect(() => {
    setSelectedDate(startOfMonth(currentMonth));
    fetchMeetings(startOfMonth(currentMonth));
    // eslint-disable-next-line
  }, [currentMonth]);

  const handleDelete = async (id) => {
    if (!id) return;
    try {
      const response = await fetch(`https://api.insightos.co.uk/admin/deletemeeting/${id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      await response.json();
      fetchMeetings(selectedDate);
    }
    catch (error) {
      console.error(error);
    }
  };

  // No AI features for admin calendar view

  const toSeconds = (time = '00:00:00') => {
    const [hour = '0', minute = '0', second = '0'] = time.split(':');
    const safeParse = (value) => {
      const parsed = parseInt(value, 10);
      return Number.isNaN(parsed) ? 0 : parsed;
    };

    return safeParse(hour) * 3600 + safeParse(minute) * 60 + safeParse(second);
  };

  const sortedMeetings = [...meetings].sort((a, b) => toSeconds(a.SlotStart || a.Time) - toSeconds(b.SlotStart || b.Time));

  return (
    <div>
      {loading && <LoadingComponent />}
      <Navbar />

      <div className="lg:grid lg:grid-cols-12 lg:gap-x-16">
          {/* Calendar Section */}
          <div className="mt-10 text-center lg:col-start-8 lg:col-end-13 lg:row-start-1 lg:mt-9 xl:col-start-9">
            <div className="flex items-center text-gray-900">
              <button
                type="button"
                onClick={previousMonth}
                className="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
              >
                <span className="sr-only">Previous month</span>
                <ChevronLeftIcon className="size-5" aria-hidden="true" />
              </button>
              <div className="flex-auto text-sm font-semibold">{format(currentMonth, 'MMMM yyyy')}</div>
              <button
                type="button"
                onClick={nextMonth}
                className="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
              >
                <span className="sr-only">Next month</span>
                <ChevronRightIcon className="size-5" aria-hidden="true" />
              </button>
            </div>

            <div className="mt-6 grid grid-cols-7 text-xs/6 text-gray-500">
              <div>M</div>
              <div>T</div>
              <div>W</div>
              <div>T</div>
              <div>F</div>
              <div>S</div>
              <div>S</div>
            </div>

            <div className="isolate mt-2 grid grid-cols-7 gap-px rounded-lg bg-gray-200 text-sm shadow ring-1 ring-gray-200">
              {days.map((day, dayIdx) => (
                <button
                  key={day.toISOString()}
                  onClick={() => {
                    setSelectedDate(day);
                    if (!isSameMonth(day, currentMonth)) {
                      setCurrentMonth(startOfMonth(day));
                    }
                    fetchMeetings(day);
                  }}
                  type="button"
                  className={classNames(
                    'py-1.5 hover:bg-green-500 focus:z-10',
                    isSameMonth(day, currentMonth) ? 'bg-white' : 'bg-gray-50',
                    isToday(day) && 'text-indigo-600 font-bold',
                    selectedDate && day.toDateString() === selectedDate.toDateString() && 'bg-indigo-600 text-white',
                    dayIdx === 0 && 'rounded-tl-lg',
                    dayIdx === 6 && 'rounded-tr-lg',
                    dayIdx === days.length - 7 && 'rounded-bl-lg',
                    dayIdx === days.length - 1 && 'rounded-br-lg'
                  )}
                >
                  <time dateTime={day.toISOString()} className="mx-auto flex size-7 items-center justify-center rounded-full">
                    {format(day, 'd')}
                  </time>
                </button>
              ))}
            </div>
          </div>

          {/* meetings Section */}
          <div className="mt-4 text-sm lg:col-span-7 xl:col-span-8">
            <h2 className="text-base font-semibold text-gray-900">Meeting Schedule</h2>

            <div className="mt-3 mb-3">
              <button
                onClick={() => setShowAppointmentForm(!showAppointmentForm)}
                className="inline-flex items-center gap-x-1.5 rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700"
              >
                {showAppointmentForm ? 'Hide Create Appointments' : 'Create Appointments'}
              </button>
            </div>

            {showAppointmentForm && (
              <div className="flex flex-col gap-4 mt-3 mb-3">
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700">Select Date</label>
                    <input
                      type="date"
                      value={appointmentDate}
                      onChange={(e) => setAppointmentDate(e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                    />
                  </div>

                  <div className="w-48">
                    <label className="block text-sm font-medium text-gray-700">Created By</label>
                    <select
                      value={createdBy}
                      onChange={(e) => setCreatedBy(e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 bg-white py-2 px-2 shadow-sm sm:text-sm"
                    >
                      <option>John</option>
                      <option>Ashley Cook</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Pick Time Slots (multi-select)</label>
                  <div className="mt-2 grid grid-cols-4 gap-2">
                    {(() => {
                      const times = [];
                      // from 09:00 to 22:00 in 30 min increments
                      for (let h = 9; h <= 22; h++) {
                        for (let m of [0, 30]) {
                          if (h === 22 && m === 30) continue; // stop at 22:00
                          const hh = String(h).padStart(2, '0');
                          const mm = String(m).padStart(2, '0');
                          times.push(`${hh}:${mm}`);
                        }
                      }
                      return times.map((t) => {
                        const active = selectedTimes.includes(t);
                        return (
                          <button
                            key={t}
                            type="button"
                            onClick={() => {
                              setSelectedTimes((prev) =>
                                prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]
                              );
                            }}
                            className={`py-2 px-2 rounded-md text-sm font-medium ${active ? 'bg-indigo-600 text-white' : 'bg-white text-gray-800 ring-1 ring-gray-200 hover:bg-gray-50'}`}
                          >
                            {t}
                          </button>
                        );
                      });
                    })()}
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={async () => {
                      if (!appointmentDate || selectedTimes.length === 0) return;
                      setLoading(true);
                      try {
                        for (const time of selectedTimes) {
                          const body = {
                            MeetingDate: appointmentDate,
                            MeetingTime: time,
                            CreatedBy: createdBy,
                            InternalSlot: null,

                          };
                          await fetch('https://api.insightos.co.uk/admin/appointmentsetting', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            credentials: 'include',
                            body: JSON.stringify(body),
                          });
                        }
                        // clear selection and refresh
                        setSelectedTimes([]);
                        const parsedAppointmentDate = parseISO(appointmentDate);
                        setSelectedDate(parsedAppointmentDate);
                        setCurrentMonth(startOfMonth(parsedAppointmentDate));
                        fetchMeetings(parsedAppointmentDate);
                      } catch (err) {
                        console.error('Failed creating appointments', err);
                      } finally {
                        setLoading(false);
                      }
                    }}
                    className="inline-flex items-center gap-x-1.5 rounded-md bg-emerald-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700"
                  >
                    Save
                  </button>

                  <button
                    onClick={() => {
                      setSelectedTimes([]);
                    }}
                    className="inline-flex items-center gap-x-1.5 rounded-md bg-gray-200 px-3 py-2 text-sm font-semibold text-gray-800 shadow-sm hover:bg-gray-300"
                  >
                    Clear
                  </button>
                </div>
              </div>
            )}
            <div className="mt-3">
              {user && user.googleCalendarPermission ? (
                <button disabled className="flex-1 inline-flex items-center justify-center gap-x-1.5 rounded-md bg-green-100 px-3 py-2 text-sm font-semibold text-green-700 shadow-sm ring-1 ring-inset ring-green-600/20">
                  Google Calendar Synced
                </button>
              ) : (
                <button
                  onClick={() => window.location.href = 'https://api.insightos.co.uk/api/googlesync'}
                  className="flex-1 inline-flex items-center justify-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                >
                  <img className="h-5 w-5" src="https://www.svgrepo.com/show/475656/google-color.svg" alt="Google" />
                  Sync Google Calendar
                </button>
              )}
            </div>

            {/* Meetings List */}
            <ol>
            {meetings.length > 0 ? (
              sortedMeetings.map((meeting) => {
                // Background based on InternalSlot and MeetingType
                let meetingBackgroundColor = 'bg-gray-100';
                const internalSlotValue = normalizeInternalSlot(meeting.InternalSlot);
                if (internalSlotValue === null) meetingBackgroundColor = 'bg-red-200';
                else if (internalSlotValue === 1) meetingBackgroundColor = 'bg-emerald-200';
                else {
                  if (meeting.MeetingType === 'Training') meetingBackgroundColor = 'bg-blue-200';
                  else if (meeting.MeetingType === 'Sales') meetingBackgroundColor = 'bg-yellow-200';
                  else if (meeting.MeetingType === 'Onboarding') meetingBackgroundColor = 'bg-emerald-200';
                }

                const startTime = meeting.SlotStart || meeting.Time || meeting.MeetingStart || '';
                const endTime = meeting.SlotEnd || meeting.FinishTime || meeting.MeetingEnd || '';

                return (
                  <li key={getMeetingId(meeting) ?? `${meeting.CompanyName || meeting.Name}-${startTime}`} className={`relative mt-5 rounded-lg border ${meetingBackgroundColor} p-4 shadow-md`}>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <h3 className="font-bold text-lg text-indigo-700">{meeting.CompanyName || meeting.Name}</h3>
                        <div className="flex items-center space-x-2 mt-1">
                          <ClockIcon className="h-5 w-5 text-indigo-700" />
                          <span className="font-semibold">{startTime} {endTime ? `- ${endTime}` : ''}</span>
                        </div>
                      </div>
                      <div>
                        {meeting.MeetingType && (
                          <div className="flex items-center space-x-2">
                            <MapPinIcon className="h-5 w-5 text-black" />
                            <span className="font-semibold">Google Meet</span>
                          </div>
                        )}
                        <div className="flex items-center space-x-2 mt-2">
                          <UserCircleIcon className="h-5 w-5 text-black" />
                          <span className="font-semibold">{meeting.Attendees}</span>
                          <span className= "text-gray-600">({meeting.CreatedBy})</span>
                        </div>
                      </div>
                      <div className="text-right md:text-right">
                        <span className="block text-sm text-gray-600">Type: {meeting.MeetingType || 'Open Slot'}</span>
                      </div>
                    </div>

                    <div className="mt-3">
                      <span className="block text-gray-600 font-medium">Notes:</span>
                      <p className="text-gray-800 mt-1">{meeting.Notes}</p>
                    </div>

                    <div className="mt-4 flex justify-end space-x-3">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={internalSlotValue === 1}
                          onChange={async () => {
                            const meetingId = getMeetingId(meeting);
                            if (!meetingId) return;
                            const newValue = internalSlotValue === 1 ? null : 1;
                            try {
                              await fetch('https://api.insightos.co.uk/admin/internalslot', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                credentials: 'include',
                                body: JSON.stringify({ id: meetingId, internalSlot: newValue }),
                              });
                              fetchMeetings(selectedDate);
                            } catch (err) {
                              console.error('Failed to update internal slot', err);
                            }
                          }}
                        />
                        <span className="text-sm font-medium">Internal Slot</span>
                      </label>
                      <button
                        onClick={() => handleDelete(getMeetingId(meeting))}
                        type="button"
                        className="inline-flex items-center gap-x-1.5 rounded-md bg-red-700 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-red-600"
                      >
                        Cancel Meeting
                        <XCircleIcon className="h-5 w-5" />
                      </button>
                      {meeting.MeetingType && (
                        <button
                          onClick={() => {
                            try {
                              if (meeting.GoogleMeetUrl) window.open(meeting.GoogleMeetUrl, '_blank');
                            } catch (err) {
                              console.error('Failed to open Google Meet URL', err);
                            }
                           // open up new tab sending to https://docs.insightos.co.uk
                           window.open('https://docs.insightos.co.uk', '_blank');
                          }}
                          className="rounded-md bg-[#1a73e8] px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-[#1557b0] flex items-center gap-2 justify-center"
                        >
                          <img src="https://fonts.gstatic.com/s/i/productlogos/meet_2020q4/v6/web-512dp/logo_meet_2020q4_color_1x_web_512dp.png" alt="Meet" className="h-5 w-5 bg-white rounded-full p-0.5" />
                          Join
                        </button>
                      )}
                    </div>
                  </li>
                );
              })
            ) : (
              <div className="rounded-md bg-gray-100 p-4 text-center text-gray-700">No Meetings for this day.</div>
            )}
            </ol>
          </div>
        </div>
    </div>
  );
}
